import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Initial state
const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  sessionData: null,
  retryCount: 0,
  isRetrying: false,
  lastSignInAttempt: null,
  authMetrics: {
    signInAttempts: 0,
    signInSuccesses: 0,
    averageSignInTime: 0,
    lastSignInTime: null,
  },
};

// Async thunks for authentication
export const signInWithGoogle = createAsyncThunk(
  'auth/signInWithGoogle',
  async (_, { rejectWithValue }) => {
    const startTime = Date.now();
    try {
      const { signInWithPopup, GoogleAuthProvider } = await import(
        'firebase/auth'
      );
      const { auth } = await import('../../firebase');

      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'consent',
      });

      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      return {
        user: {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          emailVerified: user.emailVerified,
        },
        signInTime: Date.now() - startTime,
      };
    } catch (error) {
      return rejectWithValue({
        code: error.code,
        message: error.message,
        timestamp: new Date().toISOString(),
      });
    }
  }
);

export const signOut = createAsyncThunk(
  'auth/signOut',
  async (_, { rejectWithValue }) => {
    try {
      const { signOut: firebaseSignOut } = await import('firebase/auth');
      const { auth } = await import('../../firebase');

      await firebaseSignOut(auth);

      // Clear performance metrics
      // Performance monitor reset

      return null;
    } catch (error) {
      return rejectWithValue({
        code: error.code,
        message: error.message,
      });
    }
  }
);

export const checkAuthState = createAsyncThunk(
  'auth/checkAuthState',
  async (_, { rejectWithValue }) => {
    try {
      const { onAuthStateChanged } = await import('firebase/auth');
      const { auth } = await import('../../firebase');

      return new Promise((resolve, reject) => {
        let resolved = false;
        const timeoutId = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            unsubscribe();
            resolve({ user: null });
          }
        }, 2000);

        const unsubscribe = onAuthStateChanged(
          auth,
          user => {
            if (!resolved) {
              resolved = true;
              clearTimeout(timeoutId);
              unsubscribe();
              if (user) {
                resolve({
                  user: {
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName,
                    photoURL: user.photoURL,
                    emailVerified: user.emailVerified,
                  },
                });
              } else {
                resolve({ user: null });
              }
            }
          },
          error => {
            if (!resolved) {
              resolved = true;
              clearTimeout(timeoutId);
              unsubscribe();
              reject(error);
            }
          }
        );
      });
    } catch (error) {
      return rejectWithValue({
        code: error.code,
        message: error.message,
        timestamp: new Date().toISOString(),
      });
    }
  }
);

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    incrementRetryCount: state => {
      state.retryCount += 1;
      state.isRetrying = true;
    },
    resetRetryCount: state => {
      state.retryCount = 0;
      state.isRetrying = false;
    },
    updateSessionData: (state, action) => {
      state.sessionData = action.payload;
    },
    updateAuthMetrics: (state, action) => {
      state.authMetrics = { ...state.authMetrics, ...action.payload };
    },
    resetAuthState: state => {
      return { ...initialState };
    },
  },
  extraReducers: builder => {
    builder
      // Sign in with Google
      .addCase(signInWithGoogle.pending, state => {
        state.isLoading = true;
        state.error = null;
        state.isRetrying = true;
        state.lastSignInAttempt = Date.now();
        state.authMetrics.signInAttempts += 1;
      })
      .addCase(signInWithGoogle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.error = null;
        state.isRetrying = false;
        state.retryCount = 0;
        state.authMetrics.signInSuccesses += 1;
        state.authMetrics.lastSignInTime = Date.now();
        state.authMetrics.averageSignInTime =
          (state.authMetrics.averageSignInTime *
            (state.authMetrics.signInSuccesses - 1) +
            action.payload.signInTime) /
          state.authMetrics.signInSuccesses;
      })
      .addCase(signInWithGoogle.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.error = action.payload;
        state.isRetrying = false;
      })

      // Sign out
      .addCase(signOut.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signOut.fulfilled, state => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.error = null;
        state.retryCount = 0;
        state.isRetrying = false;
        state.sessionData = null;
        state.authMetrics = initialState.authMetrics;
      })
      .addCase(signOut.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Check auth state
      .addCase(checkAuthState.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkAuthState.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.isAuthenticated = !!action.payload.user;
        state.error = null;
      })
      .addCase(checkAuthState.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

// Export actions
export const {
  clearError,
  incrementRetryCount,
  resetRetryCount,
  updateSessionData,
  updateAuthMetrics,
  resetAuthState,
} = authSlice.actions;

// Selectors
export const selectAuth = state => state.auth;
export const selectUser = state => state.auth.user;
export const selectIsAuthenticated = state => state.auth.isAuthenticated;
export const selectAuthLoading = state => state.auth.isLoading;
export const selectAuthError = state => state.auth.error;
export const selectAuthMetrics = state => state.auth.authMetrics;
export const selectRetryCount = state => state.auth.retryCount;
export const selectIsRetrying = state => state.auth.isRetrying;

// Export reducer
export default authSlice.reducer;
